import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Play,
  Pause,
  Square,
  Filter,
  Search,
  Plus,
  Timer,
  Target,
  TrendingUp,
  ChevronRight,
  Eye,
  Edit,
  MoreHorizontal,
  ArrowRight,
  Users
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import { TaskWorkflowProgressCompact, TaskWorkflowStatus } from './components/TaskWorkflowProgress';
import { useTimer } from '../../contexts/TimerContext';
import { UserTaskDetailsModal } from './UserTaskDetailsModal';
import { TaskReallocationModal } from './TaskReallocationModal';
import { ReworkReasonModal } from './components/ReworkReasonModal';
import { RecurrenceBadge } from '../../components/ui/RecurrenceBadge';
import toast from 'react-hot-toast';

interface Task {
  id: string;
  title: string;
  description?: string;
  clientName: string;
  status: 'draft' | 'in_progress' | 'in_review' | 'ready_to_submit' | 'submitted' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  assignedRole: 'preparer' | 'reviewer' | 'submitter';
  timeSpent: number; // in minutes
  estimatedTime?: number; // in minutes
  completionPercentage: number;
  type: string;
  createdAt: string;
  assignees?: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  previouslyWorkedUsers?: Array<{
    id: string;
    fullName: string;
    roles: string[];
    isCurrentlyAssigned?: boolean;
    lastWorkedAt: string;
  }>;
  reallocationHistory?: Array<{
    at: string;
    by: {
      id: string;
      fullName: string;
    };
    fromUser?: {
      id: string;
      fullName: string;
    };
    toUser: {
      id: string;
      fullName: string;
    };
    role: 'preparer' | 'reviewer' | 'submitter';
    comment?: string;
    reason?: string;
  }>;
  recurrenceInfo?: {
    isRecurring: boolean;
    description: string;
    nextDueDates: string[];
  };
}

interface TaskStats {
  total: number;
  draft: number;
  inProgress: number;
  inReview: number;
  readyToSubmit: number;
  submitted: number;
  completed: number;
  overdue: number;
  assigned: number;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'in_review': return 'bg-purple-100 text-purple-800';
      case 'ready_to_submit': return 'bg-blue-100 text-blue-800';
      case 'submitted': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      // Legacy status support
      case 'TODO': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'REVIEW': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'SUBMITTED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'in_progress': return 'In Progress';
      case 'in_review': return 'In Review';
      case 'ready_to_submit': return 'Ready to Submit';
      case 'submitted': return 'Submitted';
      case 'completed': return 'Completed';
      case 'blocked': return 'Blocked';
      default: return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
      {getStatusLabel(status)}
    </span>
  );
};

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <span className={`text-xs font-medium ${getPriorityColor(priority)}`}>
      {priority.toUpperCase()}
    </span>
  );
};

// Helper function to format duration in seconds to HH:MM:SS
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const TaskCard: React.FC<{
  task: Task;
  onStartTimer: (taskId: string) => void;
  onStopTimer: (taskId: string) => void;
  activeTimerTaskId: string | null;
  elapsedTime: number;
  onStatusUpdate: (taskId: string, action: string) => void;
  onViewDetails: (taskId: string) => void;
  onReallocateTask: (taskId: string, taskTitle: string, currentRole: 'preparer' | 'reviewer' | 'submitter') => void;
  currentUserId: string;
}> = ({ task, onStartTimer, onStopTimer, activeTimerTaskId, elapsedTime, onStatusUpdate, onViewDetails, onReallocateTask, currentUserId }) => {
  // Simplified timer comparison - normalize both IDs to strings for consistent comparison
  const isActiveTimer = activeTimerTaskId !== null && String(activeTimerTaskId) === String(task.id);
  const isOverdue = new Date(task.dueDate) < new Date() && !['completed', 'submitted'].includes(task.status);
  const daysUntilDue = Math.ceil((new Date(task.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  // Helper function to extract ID from assignee (could be string ID, user object, or serialized string)
  const getIdFromAssignee = (assignee: any): string | null => {
    if (!assignee) return null;

    // If it's a string that looks like a serialized object, extract the ObjectId
    if (typeof assignee === 'string') {
      // Check if it's a serialized object string
      if (assignee.includes('ObjectId(')) {
        // Extract the ObjectId value using regex
        const match = assignee.match(/ObjectId\('([^']+)'\)/);
        if (match && match[1]) {
          return match[1];
        }
      }
      // If it's just a plain string ID, return it
      return assignee;
    }

    // If it's a user object with _id, extract the _id
    if (assignee._id) return assignee._id.toString();

    // If it's an ObjectId, convert to string
    return assignee.toString();
  };

  // Determine user's role for this task
  const preparerId = getIdFromAssignee(task.assignees?.preparerId);
  const reviewerId = getIdFromAssignee(task.assignees?.reviewerId);
  const submitterId = getIdFromAssignee(task.assignees?.submitterId);

  const userRole = preparerId === currentUserId ? 'preparer' :
                   reviewerId === currentUserId ? 'reviewer' :
                   submitterId === currentUserId ? 'submitter' : null;



  // Get available workflow actions based on current status and user role
  const getWorkflowActions = () => {
    const actions = [];

    if (userRole === 'preparer') {
      if (task.status === 'draft') {
        actions.push({ action: 'start-work', label: 'Start Work', color: 'bg-blue-600 hover:bg-blue-700' });
      } else if (task.status === 'in_progress') {
        actions.push({ action: 'submit-for-review', label: 'Submit for Review', color: 'bg-green-600 hover:bg-green-700' });
      }
    } else if (userRole === 'reviewer') {
      if (task.status === 'in_review') {
        actions.push({ action: 'complete-review', label: 'Review Completed', color: 'bg-purple-600 hover:bg-purple-700' });
        actions.push({ action: 'return-to-preparer', label: 'Return to Preparer', color: 'bg-orange-600 hover:bg-orange-700', requiresReason: true });
      }
    } else if (userRole === 'submitter') {
      if (task.status === 'ready_to_submit') {
        actions.push({ action: 'submit-task', label: 'Submit', color: 'bg-green-600 hover:bg-green-700' });
        actions.push({ action: 'return-to-reviewer', label: 'Return to Reviewer', color: 'bg-orange-600 hover:bg-orange-700', requiresReason: true });
      }
    }

    return actions;
  };

  const workflowActions = getWorkflowActions();

  return (
    <div className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow ${
      isActiveTimer ? 'border-green-300 bg-green-50' :
      isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="font-medium text-gray-900">{task.title}</h3>
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
            {task.recurrenceInfo && (
              <RecurrenceBadge recurrenceInfo={task.recurrenceInfo} size="sm" />
            )}
            {isActiveTimer && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                <Timer className="w-3 h-3 mr-1" />
                Timer Active
              </span>
            )}
          </div>
          <div className="mb-2">
            <TaskWorkflowProgressCompact
              status={task.status}
              assignees={task.assignees}
              currentUserId={currentUserId}
            />
          </div>
          <p className="text-sm text-gray-600 mb-2">{task.clientName} • {task.type}</p>
          {task.description && (
            <p className="text-sm text-gray-500 mb-2 line-clamp-2">{task.description}</p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Workflow Action Buttons */}
          {workflowActions.map((action) => (
            <button
              key={action.action}
              onClick={() => onStatusUpdate(task.id, action.action)}
              className={`px-3 py-1 text-xs font-medium text-white rounded-lg ${action.color}`}
              title={action.label}
            >
              {action.label}
            </button>
          ))}

          {/* Timer Controls */}
          {isActiveTimer ? (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 px-2 py-1 bg-green-50 rounded-lg">
                <Timer className="w-3 h-3 text-green-600" />
                <span className="text-xs font-mono text-green-700">
                  {formatDuration(elapsedTime)}
                </span>
              </div>
              <button
                onClick={() => onStopTimer(task.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Stop Timer"
              >
                <Square className="w-4 h-4 fill-current" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => onStartTimer(task.id)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
              title="Start Timer"
            >
              <Play className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={() => onViewDetails(task.id)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>

          <button
            onClick={() => onReallocateTask(task.id, task.title, task.assignedRole)}
            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
            title="Reallocate Task"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Previously Worked Users */}
      {task.previouslyWorkedUsers && task.previouslyWorkedUsers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-600">Previously Worked:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {task.previouslyWorkedUsers.slice(0, 3).map(user => (
              <span
                key={user.id}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  user.isCurrentlyAssigned
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
                title={`${user.fullName} - Roles: ${user.roles.join(', ')}`}
              >
                {user.fullName}
                {user.isCurrentlyAssigned && (
                  <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </span>
            ))}
            {task.previouslyWorkedUsers.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                +{task.previouslyWorkedUsers.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Reallocation History Indicator */}
      {task.reallocationHistory && task.reallocationHistory.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-medium text-blue-600">
              Reallocated {task.reallocationHistory.length} time{task.reallocationHistory.length > 1 ? 's' : ''}
            </span>
            {task.reallocationHistory[task.reallocationHistory.length - 1].comment && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Latest: "{task.reallocationHistory[task.reallocationHistory.length - 1].comment?.substring(0, 30)}
                {task.reallocationHistory[task.reallocationHistory.length - 1].comment!.length > 30 ? '...' : ''}"
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
              {isOverdue ? 'Overdue' : daysUntilDue === 0 ? 'Due today' : `${daysUntilDue} days`}
            </span>
          </div>
          <div className="flex items-center text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            <span>
              {task.timeSpent >= 60
                ? `${Math.floor(task.timeSpent / 60)}h ${task.timeSpent % 60}m spent`
                : `${task.timeSpent}m spent`
              }
            </span>
          </div>
          {task.estimatedTime && (
            <div className="flex items-center text-gray-500">
              <Target className="w-4 h-4 mr-1" />
              <span>{Math.round(task.estimatedTime / 60)}h est.</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full"
              style={{ width: `${task.completionPercentage}%` }}
            ></div>
          </div>
          <span className="text-xs text-gray-500">{task.completionPercentage}%</span>
        </div>
      </div>




    </div>
  );
};

export const UserTasks: React.FC = () => {
  const { user } = useAuthStore();
  const timeTracker = useTimer();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats>({
    total: 0,
    draft: 0,
    inProgress: 0,
    inReview: 0,
    readyToSubmit: 0,
    submitted: 0,
    completed: 0,
    overdue: 0,
    assigned: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [taskDetailsModal, setTaskDetailsModal] = useState<{ isOpen: boolean; taskId: string | null }>({
    isOpen: false,
    taskId: null
  });

  // Task reallocation modal state
  const [reallocationModal, setReallocationModal] = useState({
    isOpen: false,
    taskId: null as string | null,
    taskTitle: '',
    currentRole: 'preparer' as 'preparer' | 'reviewer' | 'submitter',
  });

  // Rework reason modal state
  const [reworkModal, setReworkModal] = useState({
    isOpen: false,
    taskId: null as string | null,
    action: '' as string,
    title: '',
    description: '',
    loading: false,
  });

  const timerStateRef = useRef<{ activeTimer: any, elapsedTime: number }>({ activeTimer: null, elapsedTime: 0 });

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [tasks]);

  // Update ref with current timer state and force re-render when timer state changes
  useEffect(() => {
    // Update ref with current timer state
    timerStateRef.current = { activeTimer: timeTracker.activeTimer, elapsedTime: timeTracker.elapsedTime };

    // Force re-render when timer state changes
    setForceUpdate(prev => prev + 1);
  }, [timeTracker.activeTimer, timeTracker.elapsedTime]);



  const fetchTasks = async () => {
    try {
      setLoading(true);
      // Add cache-busting parameter
      const timestamp = Date.now();
      // Try workflow endpoint first, fallback to assigned tasks
      try {
        const response = await api.get(`/tasks/workflow?_t=${timestamp}`);
        if (response.data.success) {
          const tasksData = response.data.data || [];
          setTasks(tasksData);
        }
      } catch (workflowError) {
        try {
          const fallbackResponse = await api.get(`/tasks/assigned?_t=${timestamp}`);
          if (fallbackResponse.data.success) {
            const tasksData = fallbackResponse.data.data || [];
            setTasks(tasksData);
          }
        } catch (assignedError) {
          console.error('Error fetching tasks:', assignedError);
          toast.error('Failed to load tasks');
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const stats = {
      total: tasks.length,
      draft: tasks.filter(t => t.status === 'draft').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      inReview: tasks.filter(t => t.status === 'in_review').length,
      readyToSubmit: tasks.filter(t => t.status === 'ready_to_submit').length,
      submitted: tasks.filter(t => t.status === 'submitted').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(t =>
        new Date(t.dueDate) < new Date() &&
        !['completed', 'submitted'].includes(t.status)
      ).length,
      assigned: tasks.length, // All tasks are assigned to the current user
    };
    setStats(stats);
  };



  const startTimer = async (taskId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);

      // Check if there's already an active timer
      if (timeTracker.activeTimer) {
        const currentTask = tasks.find(t => t.id === timeTracker.activeTimer?.taskId);
        const currentTaskTitle = currentTask ? currentTask.title : 'Unknown task';

        const confirmed = window.confirm(
          `You already have a timer running for "${currentTaskTitle}". ` +
          `Do you want to stop it and start a new timer for this task?`
        );

        if (confirmed) {
          // Stop the current timer first
          await timeTracker.stopTimer();
          // Small delay to ensure the stop operation completes
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          return; // User cancelled
        }
      }

      const description = task ? `Working on: ${task.title}` : 'Timer session';
      await timeTracker.startTimer(taskId, description);

      // Force reload of active timer to ensure state is synced
      await timeTracker.loadActiveTimer();

      // Wait a bit for state to update and then force re-render
      setTimeout(() => {
        setForceUpdate(prev => prev + 1);
      }, 100);
    } catch (error: any) {
      console.error('Error starting timer:', error);

      // Handle specific error cases
      if (error.response?.data?.error?.message?.includes('active timer')) {
        toast.error('You already have an active timer running. Please stop it first.');
      } else {
        toast.error('Failed to start timer. Please try again.');
      }
    }
  };

  const stopTimer = async (taskId: string) => {
    try {
      await timeTracker.stopTimer();
      // Small delay to ensure backend has processed the update
      await new Promise(resolve => setTimeout(resolve, 500));
      // Refresh tasks to show updated time spent
      await fetchTasks();
    } catch (error) {
      console.error('Error stopping timer:', error);
    }
  };

  const handleStatusUpdate = async (taskId: string, action: string) => {
    // Check if this action requires a reason (rework actions)
    if (action === 'return-to-preparer' || action === 'return-to-reviewer') {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const modalConfig = {
        'return-to-preparer': {
          title: 'Return Task to Preparer',
          description: 'Please provide a reason for returning this task to the preparer for rework.',
        },
        'return-to-reviewer': {
          title: 'Return Task to Reviewer',
          description: 'Please provide a reason for returning this task to the reviewer for rework.',
        },
      };

      setReworkModal({
        isOpen: true,
        taskId,
        action,
        title: modalConfig[action].title,
        description: modalConfig[action].description,
        loading: false,
      });
      return;
    }

    // Handle regular status updates
    try {
      const response = await api.patch(`/tasks/${taskId}/${action}`);
      if (response.data.success) {
        // Refresh tasks to get updated status
        await fetchTasks();

        // Show success message based on action
        const messages: Record<string, string> = {
          'start-work': 'Work started successfully',
          'submit-for-review': 'Task submitted for review',
          'complete-review': 'Review completed successfully',
          'submit-task': 'Task submitted successfully. Invoice will be generated automatically.',
        };

        toast.success(messages[action] || 'Status updated successfully');
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      toast.error(error.response?.data?.message || 'Failed to update task status');
    }
  };

  const handleViewDetails = (taskId: string) => {
    setTaskDetailsModal({ isOpen: true, taskId });
  };

  const handleCloseTaskDetails = () => {
    setTaskDetailsModal({ isOpen: false, taskId: null });
  };

  const handleReallocateTask = (taskId: string, taskTitle: string, currentRole: 'preparer' | 'reviewer' | 'submitter') => {
    setReallocationModal({
      isOpen: true,
      taskId,
      taskTitle,
      currentRole
    });
  };

  const handleCloseReallocation = () => {
    setReallocationModal({
      isOpen: false,
      taskId: null,
      taskTitle: '',
      currentRole: 'preparer'
    });
  };

  const handleReallocationSuccess = () => {
    fetchTasks(); // Refresh tasks to get updated assignments
  };

  const handleReworkConfirm = async (reason: string) => {
    if (!reworkModal.taskId || !reworkModal.action) return;

    setReworkModal(prev => ({ ...prev, loading: true }));

    try {
      const response = await api.patch(`/tasks/${reworkModal.taskId}/${reworkModal.action}`, {
        reason,
      });

      if (response.data.success) {
        // Refresh tasks to get updated status
        await fetchTasks();

        // Show success message
        const messages: Record<string, string> = {
          'return-to-preparer': 'Task returned to preparer for rework',
          'return-to-reviewer': 'Task returned to reviewer for rework',
        };

        toast.success(messages[reworkModal.action] || 'Task returned successfully');

        // Close modal
        setReworkModal({
          isOpen: false,
          taskId: null,
          action: '',
          title: '',
          description: '',
          loading: false,
        });
      }
    } catch (error: any) {
      console.error('Error returning task:', error);
      toast.error(error.response?.data?.message || 'Failed to return task');
    } finally {
      setReworkModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleReworkCancel = () => {
    setReworkModal({
      isOpen: false,
      taskId: null,
      action: '',
      title: '',
      description: '',
      loading: false,
    });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">My Tasks</h2>
              <p className="text-gray-600">
                Manage and track your assigned tasks and deadlines.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">To Do</p>
                <p className="text-2xl font-bold text-blue-600">{stats.assigned}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Review</p>
                <p className="text-2xl font-bold text-purple-600">{stats.inReview}</p>
              </div>
              <Eye className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">Review</option>
                <option value="COMPLETED">Completed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                <Plus className="w-4 h-4 mr-2" />
                Request Task
              </button>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {filteredTasks.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? 'Try adjusting your filters to see more tasks.'
                  : 'You don\'t have any assigned tasks yet.'}
              </p>
            </div>
          ) : (
            filteredTasks.map((task) => {
              // Extract timer state for this specific task using ref
              const activeTimerTaskId = timerStateRef.current.activeTimer?.taskId || null;
              const isThisTaskActive = activeTimerTaskId !== null && String(activeTimerTaskId) === String(task.id);
              const taskElapsedTime = isThisTaskActive ? timerStateRef.current.elapsedTime : 0;

              return (
                <TaskCard
                  key={`${task.id}-${forceUpdate}`}
                  task={task}
                  onStartTimer={startTimer}
                  onStopTimer={stopTimer}
                  activeTimerTaskId={activeTimerTaskId}
                  elapsedTime={taskElapsedTime}
                  onStatusUpdate={handleStatusUpdate}
                  onViewDetails={handleViewDetails}
                  onReallocateTask={handleReallocateTask}
                  currentUserId={user?.id || ''}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Task Details Modal */}
      <UserTaskDetailsModal
        isOpen={taskDetailsModal.isOpen}
        onClose={handleCloseTaskDetails}
        taskId={taskDetailsModal.taskId}
      />

      {/* Task Reallocation Modal */}
      <TaskReallocationModal
        isOpen={reallocationModal.isOpen}
        onClose={handleCloseReallocation}
        onSuccess={handleReallocationSuccess}
        taskId={reallocationModal.taskId}
        taskTitle={reallocationModal.taskTitle}
        currentRole={reallocationModal.currentRole}
      />

      {/* Rework Reason Modal */}
      <ReworkReasonModal
        isOpen={reworkModal.isOpen}
        onClose={handleReworkCancel}
        onConfirm={handleReworkConfirm}
        title={reworkModal.title}
        description={reworkModal.description}
        loading={reworkModal.loading}
      />
    </UserDashboardLayout>
  );
};

export default UserTasks;
